import React, { useEffect, useMemo, useRef, useState } from 'react';
import { decode as decodePng } from 'fast-png';
import GradedCanvas from './GradedCanvas';
import { DEFAULT_GRADE, isIdentityGrade } from '../utils/grade';
import {
  useMovieStills,
  requestDevelop,
  requestPhotoGrade,
  renameAsset,
  updateAssetTags,
} from '../data/firestore';

// Delivery formats the still bake worker can emit (mirrors PhotoEditor).
const EXPORT_OPTIONS = [
  { id: 'fulljpeg', label: 'Full-res JPEG', hint: 'sRGB · max quality' },
  { id: 'tiff16', label: '16-bit TIFF', hint: 'archive · print' },
  { id: 'png8', label: 'PNG', hint: 'lossless 8-bit' },
  { id: 'webp', label: 'WebP', hint: 'smaller web file' },
  { id: 'magazine', label: 'Magazine JPEG', hint: 'long edge 3000 · Q90' },
];

// Aspect choices for the "Custom aspect" version. `r` is width/height; 'src'
// locks to the original frame shape (a centered zoom, not a reshape).
const CROP_ASPECTS = [
  { id: 'source', label: 'Original', r: 'src' },
  { id: '3:2', label: '3:2', r: 3 / 2 },
  { id: '16:9', label: '16:9', r: 16 / 9 },
  { id: '2.39:1', label: '2.39:1', r: 2.39 },
  { id: '4:5', label: '4:5', r: 4 / 5 },
  { id: '1:1', label: '1:1', r: 1 },
  { id: '9:16', label: '9:16', r: 9 / 16 },
];

const clampN = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Centered crop rect {x,y,w,h} for a target aspect inside a source of `srcAR`.
function rectForAR(targetAR, scale, srcAR) {
  if (!targetAR || !srcAR) return null;
  const r = targetAR / srcAR;
  const maxW = r >= 1 ? 1 : r;
  const maxH = r >= 1 ? 1 / r : 1;
  const w = clampN(scale, 0.1, 1) * maxW;
  const h = clampN(scale, 0.1, 1) * maxH;
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}

// The four export "versions" of a grabbed still.
const VERSIONS = [
  { id: 'raw', label: 'Raw', hint: 'Original frame — no edits' },
  { id: 'color', label: 'Color & light', hint: 'Graded, full frame (no crop)' },
  { id: 'reframe', label: 'Color · light · reframe', hint: 'Graded + your saved crop' },
  { id: 'custom', label: 'Custom aspect', hint: 'Graded + a crop you choose' },
];

function srcTimeLabel(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

// ---- the expanded export panel for the selected still ----------------------
function StillDetail({ still }) {
  const tripId = still.tripId;
  const assetId = still.id;
  const develop = still.develop || {};
  const gradeRender = still.gradeRender || {};
  const proxyUrl = still.proxy16Url || '';
  const savedGrade = still.grade || null;
  const savedCrop = still.crop && Number(still.crop.w) > 0 ? still.crop : null;
  const hasGrade = !isIdentityGrade(savedGrade || DEFAULT_GRADE);

  const [version, setVersion] = useState('reframe');
  const [aspectId, setAspectId] = useState('source');
  const [scale, setScale] = useState(0.85);
  const [fmts, setFmts] = useState({ fulljpeg: true, tiff16: false, png8: false, webp: false, magazine: false });
  const [source, setSource] = useState(null);
  const [loadErr, setLoadErr] = useState('');

  const [name, setName] = useState(still.name || '');
  const [tags, setTags] = useState(Array.isArray(still.tags) ? still.tags : []);
  const [tagDraft, setTagDraft] = useState('');
  const lastAsset = useRef(assetId);

  // Reset local drafts when the selected still changes.
  useEffect(() => {
    if (lastAsset.current !== assetId) {
      lastAsset.current = assetId;
      setName(still.name || '');
      setTags(Array.isArray(still.tags) ? still.tags : []);
      setVersion(savedCrop ? 'reframe' : hasGrade ? 'color' : 'raw');
      setSource(null);
      setLoadErr('');
    }
  }, [assetId, still.name, still.tags, savedCrop, hasGrade]);

  // Load the developed 16-bit proxy for a pixel-accurate live preview.
  useEffect(() => {
    let on = true;
    setSource(null);
    setLoadErr('');
    if (!proxyUrl) return undefined;
    (async () => {
      try {
        const res = await fetch(proxyUrl, { mode: 'cors' });
        if (!res.ok) throw new Error(`proxy ${res.status}`);
        const png = decodePng(new Uint8Array(await res.arrayBuffer()));
        if (!on) return;
        setSource({ data: png.data, width: png.width, height: png.height, channels: png.channels });
      } catch (err) {
        if (on) setLoadErr(String(err.message || err));
      }
    })();
    return () => { on = false; };
  }, [proxyUrl]);

  const srcAR = source && source.width && source.height
    ? source.width / source.height
    : (still.width && still.height ? still.width / still.height : 0);

  // The grade + crop the current version previews and bakes.
  const versionGrade = version === 'raw' ? DEFAULT_GRADE : (savedGrade || DEFAULT_GRADE);
  const customCrop = useMemo(() => {
    const target = aspectId === 'source' ? srcAR : (CROP_ASPECTS.find((a) => a.id === aspectId) || {}).r;
    return rectForAR(target, scale, srcAR);
  }, [aspectId, scale, srcAR]);
  const versionCrop =
    version === 'raw' || version === 'color' ? null
      : version === 'reframe' ? savedCrop
        : customCrop;

  const developed = develop.status === 'ready' && !!proxyUrl;
  const developing = develop.status === 'queued' || develop.status === 'developing';
  const baking = gradeRender.status === 'queued' || gradeRender.status === 'rendering';
  const selectedFmts = EXPORT_OPTIONS.filter((o) => fmts[o.id]).map((o) => o.id);

  const stageDerivatives = (still.stageDerivatives || {})[version] || {};

  const runExport = () => {
    const formats = selectedFmts.length ? selectedFmts : ['fulljpeg'];
    const stage = {
      id: version,
      grade: version === 'raw' ? 'raw' : 'graded',
      crop: versionCrop || null,
      formats,
    };
    // grade undefined → never overwrite the still's saved grade/crop; the bake
    // worker reads them for the 'graded' versions and applies the crop override.
    requestPhotoGrade(tripId, assetId, undefined, [], [stage]).catch(() => {});
  };

  const commitName = () => {
    const clean = (name || '').trim();
    if (clean && clean !== still.name) renameAsset(tripId, assetId, clean).catch(() => {});
  };
  const addTag = () => {
    const t = tagDraft.trim();
    if (!t || tags.includes(t)) { setTagDraft(''); return; }
    const next = [...tags, t];
    setTags(next);
    setTagDraft('');
    updateAssetTags(tripId, assetId, next).catch(() => {});
  };
  const removeTag = (t) => {
    const next = tags.filter((x) => x !== t);
    setTags(next);
    updateAssetTags(tripId, assetId, next).catch(() => {});
  };

  return (
    <div className="cs-still-detail">
      <div className="cs-still-detail-main">
        <div className="cs-still-stage">
          {developed && source ? (
            <GradedCanvas
              source={source}
              grade={versionGrade}
              getCrop={() => versionCrop || null}
              onError={setLoadErr}
              className="cs-still-canvas"
            />
          ) : (
            <div className="cs-still-stage-fallback">
              {still.displayUrl || still.thumbUrl ? (
                <img src={still.displayUrl || still.thumbUrl} alt={still.name} />
              ) : (
                <div className="cs-help" style={{ padding: 24 }}>Still is still rendering…</div>
              )}
              <div className="cs-still-prep">
                {loadErr ? (
                  <span className="cs-help">Preview unavailable: {loadErr}</span>
                ) : developing ? (
                  <span className="cs-help">Preparing a live preview…</span>
                ) : (
                  <>
                    <span className="cs-help">Static thumbnail. Build a live, version-accurate preview (~30s):</span>
                    <button
                      className="cs-btn cs-btn-ghost cs-btn-sm"
                      disabled={!still.s3Key}
                      onClick={() => requestDevelop(tripId, assetId).catch(() => {})}
                      title="Develop a 16-bit proxy from the RAW still for an accurate live preview (~30s)"
                    >
                      Prepare live preview · ~30s
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="cs-still-versions">
          {VERSIONS.map((v) => (
            <button
              key={v.id}
              className={`cs-btn cs-btn-sm ${version === v.id ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => setVersion(v.id)}
              title={v.hint}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div className="cs-help cs-still-vhint">
          {VERSIONS.find((v) => v.id === version)?.hint}
          {version === 'reframe' && !savedCrop ? ' — no saved crop on this still, so this is the full frame.' : ''}
          {(version === 'color' || version === 'reframe' || version === 'custom') && !hasGrade ? ' — this still has no color/light grade yet.' : ''}
        </div>

        {version === 'custom' && (
          <div className="cs-still-custom">
            <span className="cs-still-custom-label">Aspect</span>
            {CROP_ASPECTS.map((a) => (
              <button
                key={a.id}
                className={`cs-btn cs-btn-sm ${aspectId === a.id ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                onClick={() => setAspectId(a.id)}
              >
                {a.label}
              </button>
            ))}
            <span className="cs-still-zoom">
              <span className="cs-help">Zoom</span>
              <input
                type="range"
                min="0.3"
                max="1"
                step="0.01"
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
              />
            </span>
          </div>
        )}
      </div>

      <div className="cs-still-detail-side">
        <label className="cs-field" style={{ margin: 0 }}>
          <span>Name</span>
          <input
            className="cs-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitName(); } }}
            placeholder="Frame name"
          />
        </label>

        <div className="cs-field" style={{ margin: 0 }}>
          <span>Tags</span>
          <div className="cs-tag-row">
            {tags.length === 0 && <span className="cs-help" style={{ margin: 0 }}>No tags yet.</span>}
            {tags.map((t) => (
              <span key={t} className="cs-tag">
                {t}
                <button type="button" className="cs-tag-x" onClick={() => removeTag(t)} title="Remove tag">×</button>
              </span>
            ))}
          </div>
          <div className="cs-tag-add">
            <input
              className="cs-input"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              placeholder="Add a tag (e.g. barrel, drone, Alex)…"
            />
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={addTag}>Add</button>
          </div>
        </div>

        <div className="cs-field" style={{ margin: 0 }}>
          <span>Export formats</span>
          <div className="cs-still-fmts">
            {EXPORT_OPTIONS.map((o) => (
              <label key={o.id} className="cs-still-fmt" title={o.hint}>
                <input
                  type="checkbox"
                  checked={!!fmts[o.id]}
                  onChange={(e) => setFmts((s) => ({ ...s, [o.id]: e.target.checked }))}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>

        <button
          className="cs-btn cs-btn-primary"
          disabled={baking || !still.s3Key}
          onClick={runExport}
          title="Bake this version into the chosen file formats (~20–40s)"
        >
          {baking ? 'Exporting… (~30s)' : `Export ${VERSIONS.find((v) => v.id === version)?.label} · ~30s`}
        </button>

        {baking && (
          <span className="cs-help">Baking the selected formats — this runs in the background, ~20–40s.</span>
        )}
        {gradeRender.status === 'error' && (
          <span className="cs-help" style={{ color: 'var(--accent)' }}>Export failed: {gradeRender.message}</span>
        )}
        {Object.keys(stageDerivatives).length > 0 && (
          <div className="cs-still-dls">
            <span className="cs-help">Download ({VERSIONS.find((v) => v.id === version)?.label}):</span>
            {EXPORT_OPTIONS.filter((o) => stageDerivatives[o.id]).map((o) => (
              <a
                key={o.id}
                className="cs-btn cs-btn-ghost cs-btn-sm"
                href={stageDerivatives[o.id]}
                download
                target="_blank"
                rel="noreferrer"
              >
                {o.label}
              </a>
            ))}
          </div>
        )}
        <span className="cs-help" style={{ fontSize: 11 }}>
          Source: {still.lineage?.sourceName || 'clip'} · {srcTimeLabel(still.lineage?.sourceTimeSec)}
          {still.lineage?.sourceIsRaw ? ' · RED RAW' : ''}
        </span>
      </div>
    </div>
  );
}

// ---- the gallery + selection ----------------------------------------------
export default function MovieStills({ movieId, clips = [] }) {
  const stills = useMovieStills(movieId, clips);
  const [selectedId, setSelectedId] = useState(null);

  // Keep a valid selection as the list loads / changes.
  useEffect(() => {
    if (!stills.length) { setSelectedId(null); return; }
    if (!selectedId || !stills.some((s) => s.id === selectedId)) {
      setSelectedId(stills[0].id);
    }
  }, [stills, selectedId]);

  const selected = useMemo(
    () => stills.find((s) => s.id === selectedId) || null,
    [stills, selectedId]
  );

  return (
    <div className="cs-section cs-still-gallery">
      <div className="cs-section-head">
        <h3 style={{ margin: 0 }}>Frame grabs</h3>
        <span className="cs-help">
          Stills grabbed from this movie · pick a version, name & tag it, then export precise files
        </span>
      </div>

      {!stills.length ? (
        <div className="cs-empty" style={{ marginTop: 8 }}>
          No frame grabs yet. Use <b>📷 Grab &amp; export still</b> on any clip above (or the
          editor’s grab button) to capture frames here.
        </div>
      ) : (
        <>
          <div className="cs-still-tiles">
            {stills.map((s) => (
              <button
                key={s.id}
                className={`cs-still-tile ${s.id === selectedId ? 'on' : ''}`}
                onClick={() => setSelectedId(s.id)}
                title={s.name}
              >
                <span className="cs-still-thumb">
                  {s.thumbUrl || s.displayUrl ? (
                    <img src={s.thumbUrl || s.displayUrl} alt={s.name} loading="lazy" />
                  ) : (
                    <span className="cs-still-thumb-ph">{s.stillGrab?.status === 'error' ? '⚠' : '…'}</span>
                  )}
                </span>
                <span className="cs-still-tile-name">{s.name}</span>
              </button>
            ))}
          </div>
          {selected && <StillDetail key={selected.id} still={selected} />}
        </>
      )}
    </div>
  );
}
